// ============================================================================
// USETOAST.JS - HOOK PERSONALIZADO PARA USAR EL SISTEMA DE TOASTS
// ============================================================================

import { useCallback } from 'react';
import { useToastContext, TOAST_TYPES } from '../contexts/ToastContext';

/**
 * Hook principal para usar el sistema de toasts
 * Proporciona una interfaz simple y consistente
 */
function useToast() {
  const context = useToastContext();

  if (!context) {
    throw new Error('useToast debe usarse dentro de ToastProvider');
  }

  const {
    showSuccess: contextShowSuccess,
    showError: contextShowError,
    showWarning: contextShowWarning,
    showInfo: contextShowInfo,
    showConfirm: contextShowConfirm,
    addToast: contextAddToast,
    removeToast,
    clearAllToasts,
    toasts,
    getToastCount
  } = context;

  // ============================================================================
  // FUNCIONES PRINCIPALES (Interfaz simplificada)
  // ============================================================================

  const showSuccess = useCallback((message, options) => {
    return contextShowSuccess(message, options);
  }, [contextShowSuccess]);

  const showError = useCallback((message, options) => {
    return contextShowError(message, options);
  }, [contextShowError]);

  const showWarning = useCallback((message, options) => {
    return contextShowWarning(message, options);
  }, [contextShowWarning]);

  const showInfo = useCallback((message, options) => {
    return contextShowInfo(message, options);
  }, [contextShowInfo]);

  // ============================================================================
  // TOAST CON ACCIÓN "DESHACER" (undo)
  // ============================================================================
  // Muestra un toast con un botón "Deshacer" que ejecuta onUndo. Ventana de
  // 6s por defecto (alineada con el patrón deferred-delete).
  const showUndo = useCallback((message, onUndo, options = {}) => {
    return contextAddToast(message, TOAST_TYPES.SUCCESS, {
      duration: 6000,
      action: { label: 'Deshacer', onClick: onUndo },
      ...options
    });
  }, [contextAddToast]);

  // ============================================================================
  // FUNCIÓN DE CONFIRMACIÓN (Reemplaza window.confirm)
  // ============================================================================

  const confirm = useCallback(async (message, options = {}) => {
    const defaultOptions = {
      title: 'Confirmar acción',
      confirmText: 'Aceptar',
      cancelText: 'Cancelar',
      ...options
    };

    try {
      const result = await contextShowConfirm(message, defaultOptions);
      return result;
    } catch (error) {
      console.error('Error en confirmación:', error);
      return false;
    }
  }, [contextShowConfirm]);

  // ============================================================================
  // FUNCIONES DE CONVENIENCIA ESPECÍFICAS
  // ============================================================================

  // Para operaciones comunes de la aplicación
  const showFileUploaded = useCallback((fileName) => {
    return showSuccess(`Archivo "${fileName}" subido correctamente`, {
      duration: 3000
    });
  }, [showSuccess]);

  const showCommentSent = useCallback(() => {
    return showSuccess('Comentario enviado correctamente', {
      duration: 2500
    });
  }, [showSuccess]);

  const showTaskCompleted = useCallback((taskName) => {
    return showSuccess(`Tarea "${taskName}" completada exitosamente`, {
      duration: 4000
    });
  }, [showSuccess]);

  const showDocumentLinked = useCallback((docTitle) => {
    return showSuccess(`Documento "${docTitle}" vinculado exitosamente`, {
      duration: 3000
    });
  }, [showSuccess]);

  const showSprintClosed = useCallback((sprintName, stats) => {
    const message = stats 
      ? `Sprint "${sprintName}" cerrado. ${stats.completed} completadas, ${stats.moved} al backlog`
      : `Sprint "${sprintName}" cerrado exitosamente`;
      
    return showSuccess(message, {
      duration: 5000
    });
  }, [showSuccess]);

  // Para errores comunes
  const showNetworkError = useCallback((operation = 'operación') => {
    return showError(`Error de red al realizar ${operation}. Revisa tu conexión.`, {
      duration: 6000
    });
  }, [showError]);

  const showValidationError = useCallback((field) => {
    return showError(`El campo "${field}" es obligatorio`, {
      duration: 4000
    });
  }, [showError]);

  const showPermissionError = useCallback((action) => {
    return showError(`No tienes permisos para ${action}`, {
      duration: 5000
    });
  }, [showError]);

  // Para advertencias comunes
  const showUnsavedChanges = useCallback(() => {
    return showWarning('Hay cambios sin guardar', {
      duration: 4000
    });
  }, [showWarning]);

  const showLimitReached = useCallback((limit, item = 'elementos') => {
    return showWarning(`Límite alcanzado: máximo ${limit} ${item}`, {
      duration: 4000
    });
  }, [showWarning]);

  // ============================================================================
  // FUNCIONES UTILITARIAS
  // ============================================================================

  const dismiss = useCallback((toastId) => {
    return removeToast(toastId);
  }, [removeToast]);

  const dismissAll = useCallback(() => {
    return clearAllToasts();
  }, [clearAllToasts]);

  const getStats = useCallback(() => {
    return {
      total: toasts.length,
      success: getToastCount(TOAST_TYPES.SUCCESS),
      error: getToastCount(TOAST_TYPES.ERROR),
      warning: getToastCount(TOAST_TYPES.WARNING),
      info: getToastCount(TOAST_TYPES.INFO)
    };
  }, [toasts.length, getToastCount]);

  // ============================================================================
  // FUNCIÓN GENÉRICA PERSONALIZADA
  // ============================================================================

  const toast = useCallback((message, type = TOAST_TYPES.INFO, options = {}) => {
    return contextAddToast(message, type, options);
  }, [contextAddToast]);

  // ============================================================================
  // FUNCIÓN PARA REEMPLAZAR TODOS LOS ALERTS
  // ============================================================================

  // Función de migración para reemplazar alerts fácilmente
  const replaceAlert = useCallback((message, type = 'info') => {
    const typeMapping = {
      'success': TOAST_TYPES.SUCCESS,
      'error': TOAST_TYPES.ERROR, 
      'warning': TOAST_TYPES.WARNING,
      'info': TOAST_TYPES.INFO
    };

    const toastType = typeMapping[type] || TOAST_TYPES.INFO;
    return contextAddToast(message, toastType);
  }, [contextAddToast]);

  // ============================================================================
  // RETORNO DEL HOOK
  // ============================================================================

  return {
    // Funciones principales
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showUndo,
    confirm,
    toast,
    
    // Funciones específicas de la app
    showFileUploaded,
    showCommentSent,
    showTaskCompleted,
    showDocumentLinked,
    showSprintClosed,
    showNetworkError,
    showValidationError,
    showPermissionError,
    showUnsavedChanges,
    showLimitReached,
    
    // Utilidades
    dismiss,
    dismissAll,
    getStats,
    replaceAlert,
    
    // Estado
    toasts,
    hasToasts: toasts.length > 0,
    toastCount: toasts.length
  };
}

// ============================================================================
// FUNCIONES AUXILIARES EXPORTADAS
// ============================================================================

// Para usar fuera de componentes React (si es necesario)
export const createToastInstance = (contextValue) => {
  if (!contextValue) {
    throw new Error('Context value is required for createToastInstance');
  }

  return {
    showSuccess: (message, options) => contextValue.showSuccess(message, options),
    showError: (message, options) => contextValue.showError(message, options),
    showWarning: (message, options) => contextValue.showWarning(message, options),
    showInfo: (message, options) => contextValue.showInfo(message, options)
  };
};

export default useToast;